import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Play, Calendar, Eye, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/api/videos`);
      setVideos(response.data.videos || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching videos:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const shareVideo = (video) => {
    const url = `${window.location.origin}/videos/${video.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Video link copied to clipboard!");
  };

  const getVideoEmbedUrl = (url, platform) => {
    if (platform === 'youtube') {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    } else if (platform === 'vimeo') {
      const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading videos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Play className="w-16 h-16" />
          </div>
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Video Gallery
          </h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto">
            Watch our latest videos about FSBO success stories, tips, and market insights
          </p>
        </div>
      </div>

      {/* Video Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {videos.length === 0 ? (
          <div className="text-center py-20">
            <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600">No videos yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Featured Video */}
            {selectedVideo && (
              <div className="mb-12">
                <Card className="overflow-hidden shadow-2xl">
                  <div className="aspect-video bg-black">
                    <iframe
                      src={getVideoEmbedUrl(selectedVideo.url, selectedVideo.platform)}
                      title={selectedVideo.title}
                      className="w-full h-full"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  </div>
                  <div className="p-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">
                      {selectedVideo.title}
                    </h2>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {selectedVideo.description}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(selectedVideo.created_at)}
                        </div>
                        {selectedVideo.views && (
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {selectedVideo.views.toLocaleString()} views
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => shareVideo(selectedVideo)}
                        className="flex items-center text-orange-600 hover:text-orange-700"
                      >
                        <Share2 className="w-4 h-4 mr-1" />
                        Share
                      </button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Video Thumbnails Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <Card 
                  key={video.id}
                  className={`overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl ${
                    selectedVideo?.id === video.id ? 'ring-4 ring-orange-500' : ''
                  }`}
                  onClick={() => setSelectedVideo(video)}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-gray-900 group">
                    <img 
                      src={video.thumbnail_url || 'https://placehold.co/640x360/FF6B35/FFFFFF/png?text=Video'}
                      alt={video.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-orange-600 rounded-full p-4 group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    {video.category && (
                      <div className="absolute top-3 right-3 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {video.category}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition">
                      {video.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {video.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(video.created_at)}
                      </div>
                      {video.duration && (
                        <span>{video.duration}</span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 py-16 mt-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Save Thousands?</h2>
          <p className="text-xl text-orange-100 mb-8">
            Join these success stories and list your home FREE on Its-Sold.com™
          </p>
          <button 
            onClick={() => window.location.href = '/register'}
            className="bg-white text-orange-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-lg text-lg transition-colors"
          >
            Get Started - It's FREE
          </button>
        </div>
      </div>
    </div>
  );
}
